import { Stack } from "@mui/material";
import { Button } from "components/ButtonNew";
import { ComponentDownloadActionButton } from "components/ComponentActionButton";
import { Icon } from "components/Icon";
import { SelectedFileBanner } from "components/SelectedFileBanner";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { cn } from "utils/styling";

const EXCEL_ACCEPT = {
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

type EnergyAnalysisEnergyInputProps = {
  disabled?: boolean;
  headingClassName?: string;
  hideTitle?: boolean;
  hint?: string;
  isUploading?: boolean;
  onDownload?: () => void;
  onUpload: (file: File) => void;
  title?: string;
};

export const EnergyAnalysisEnergyInput = ({
  disabled,
  headingClassName,
  hideTitle,
  hint,
  isUploading,
  onDownload,
  onUpload,
  title,
}: EnergyAnalysisEnergyInputProps) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const heading = title ?? t("BuildingProfile.energyAnalysis.energyInput");
  const description = hint ?? t("BuildingProfile.energyAnalysis.uploadBoxHint");
  const isBusy = disabled || isUploading;
  const onDrop = useCallback(
    (files: File[]) => {
      const next = files[0];
      if (next) setFile(next);
    },
    []
  );
  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    accept: EXCEL_ACCEPT,
    disabled: isBusy,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop,
  });
  return (
    <Stack className="w-full min-w-0 flex-col items-stretch gap-2 text-left">
      {hideTitle ? null : (
        <h3 className={cn("font-ds-headline-md text-lds-foreground-heading-active", headingClassName)}>{heading}</h3>
      )}
      <p className="text-lds-foreground-muted font-ds-body-regular-sm">{description}</p>
      <div
        {...getRootProps({
          className: cn(
            "flex w-full min-h-0 min-w-0 flex-col items-center justify-center gap-2 rounded-ds-0-5x p-4 !border-dashed border-ds-subtle",
            isDragActive && "border-ds-accent",
            isBusy && "pointer-events-none"
          ),
        })}
      >
        <input {...getInputProps()} />
        {file ? (
          <>
            <SelectedFileBanner fileName={file.name} onRemove={isBusy ? undefined : () => setFile(null)} />
            <Button
              disabled={isBusy}
              isLoading={isUploading}
              onClick={() => onUpload(file)}
              size="sm"
              startIconName="Upload"
            >
              {t("general.uploadFile")}
            </Button>
          </>
        ) : (
          <>
            <Icon iconName="Upload" size="md" />
            <p className="text-lds-foreground-muted font-ds-body-regular-sm">{t("general.dragToUploadFile")}</p>
            <Button disabled={isBusy} onClick={open} size="sm" variant="primary">
              {t("general.browse")}
            </Button>
          </>
        )}
        <ComponentDownloadActionButton
          action={onDownload}
          actionIsDisabled={isBusy}
          actionText={t("BuildingProfile.energyAnalysis.downloadTemplate")}
        />
      </div>
    </Stack>
  );
};
